import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule, CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxAllModule } from '@syncfusion/ej2-angular-inputs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { CommonService } from '../service/common.service';
import { CredentialVaultService } from '../service/credential-vault.service';
import { LoginService } from '../service/login.service';
import { PlausibleService } from '../service/plausible.service';
import { TawkService } from '../service/tawk.service';
import { UserService } from '../service/user.service';
import { AuthBrandPanelComponent } from '../shared/auth-brand-panel/auth-brand-panel.component';
import { writeCustomizationFlags } from '../shared/customization-flags';
import { applyUserProfile } from '../service/apply-user-profile';

/**
 * Login screen component responsible for authenticating users, validating email addresses, loading user-specific session data, and initializing login-related customization and tokens.
 * @component
 * @selector app-login
 * @example
 *   <app-login [inputProp]="sample_value1" (outputEvent)="handler()"></app-login>
 * @input none
 * @output none
 * @lifecycle ngOnInit Initializes the component, redirects to the legacy login page in production, restores the saved email from session storage, and loads user role data.
 * @method onPasswordChange Updates the current password value and persists it to session storage.
 * @method togglePasswordVisibility Toggles the password field between masked and plain text.
 * @method onSubmit Validates the form, tracks the login event, submits credentials for authentication, and upon success navigates to OTP verification and loads user details.
 * @method getUserRoleDetails Fetches and stores the distinct role details for the current user.
 * @method getUserDetails Retrieves the authenticated user's profile details and triggers session, role, and customization initialization.
 * @method getCustomisationList Loads customer customization flags and role-related configuration values.
 * @method verifyEmail Validates the email format and checks whether the email exists before allowing password reset or further login flow.
 * @method signInWithSSO Navigates to the SSO login screen.
 * @method unlockAccount Navigates to the account unlock page.
 * @method closePopup Navigates to the dashboard.
 * @method showResetPassword Navigates to the reset password page.
 * @method goBack Navigates back to the email verification screen.
 * @method getPTTokenForCookies Retrieves AI-related tokens and stores them in session storage for cookie/token-based features.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, TextBoxAllModule, ButtonModule, CheckBoxModule, TranslateModule, AuthBrandPanelComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css', '../shared/styles/auth-shell.css']
})

export class LoginComponent {
  loginusername: string = '';
  emailid: string = '';
  password: string = '';
  response = {};
  errorMessage: string = '';
  loginpassword: string = '';
  email: string = '';
  loginError: boolean = false;
  resetPassword: boolean = false;
  isSubmitted: boolean = false;
  /**
   * Monotonic id of the most recent sign-in attempt. Subscribe callbacks
   * compare against it so a stale response from a superseded attempt can
   * neither wipe the vault nor overwrite newer UI state.
   */
  private loginAttempt = 0;
  /**
   * True while a sign-in request is in flight. Blocks concurrent submits so a
   * double-click cannot create two competing requests where a later failure
   * would discard an earlier success and wrongly show a login error.
   */
  loginPending = false;
  showEmailValidator: boolean = false;
  /** Toggled by the eye icon next to the password field; drives its `type` (text/password). */
  showPassword: boolean = false;
  /** Client-side format error (spaces / HTML tags) surfaced before the field is submitted. */
  passwordFormatError: string = '';
  public isLoading: boolean = false;
  public firstInterval: number = 1000;
  public secondInterval: number = 1000;
  public thirdInterval: number = 2000;
  public fourthInterval: number = 1000;
  public fifthInterval: number = 2000;
  LoginDetails: any[] = [];
  AssessorRole: any[] = [];
  PerformerReviewerRole: any[] = [];

  /**
   * Injects the collaborators used by the standard login flow.
   *
   * @param formBuilder - Builds the reactive login form.
   * @param loginService - Performs the credential and OTP exchanges.
   * @param userService - Session identity accessor and token/HMAC helper.
   * @param commonService - Shared helpers used across the auth entry points.
   * @param router - Router used for OTP and post-login navigation.
   * @param http - HTTP client for requests made outside the typed services.
   * @param plausible - Records login analytics events.
   * @param tawkService - Syncs the signed-in visitor into the support chat widget.
   * @param credentialVault - Holds credentials across the OTP step.
   * @param translate - Supplies translated validation and error messages.
   */
  constructor(
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private userService: UserService,
    private commonService: CommonService,
    private router: Router,
    private http: HttpClient,
    private plausible: PlausibleService,
    private tawkService: TawkService,
    private credentialVault: CredentialVaultService,
    private translate: TranslateService
  ) { }


  ngOnInit(): void {
    if (environment.production === true) {
      window.location.href = environment.LOGIN_URL;
      return;
    }
    this.email = sessionStorage.getItem('LoginEmail') || '';
    this.password = '';
    this.userService.getRole();

  }


  onPasswordChange(value: string) {
    // Component state only — the vault is written on submit, so half-typed or
    // abandoned passwords are never persisted.
    this.password = value;
  }

  /**
   * Toggles the password field between masked and plain text, mirroring the
   * eye icon next to it.
   * @returns {void}
   */
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Checks a candidate password against the two client-side format rules also
   * enforced server-side: no whitespace anywhere in the value, and no
   * HTML/script tags. Mirrors Login.aspx's rtValidatePassword().
   * @param value - The password value to check.
   * @returns {string} A translated error message, or an empty string when the value is acceptable.
   */
  private validatePasswordFormat(value: string): string {
    if (!value) {
      return '';
    }
    if (/\s/.test(value)) {
      return this.translate.instant('app.login.password_cannot_contain_spaces');
    }
    const htmlTagPattern = /<\s*\/?\s*[a-zA-Z][^>]*>/;
    if (htmlTagPattern.test(value)) {
      return this.translate.instant('app.login.password_cannot_contain_html_tags');
    }
    return '';
  }

  /**
  * Submits the login form, validates the user credentials, and handles successful or failed login responses.
  * @example
  * nSubmit(form)
  * undefined
  * @param {any} form - The login form object used to check validity before submitting.
  * @returns {void} No return value.
  **/
  onSubmit(form: any) {

    this.isSubmitted = true;
    if (form.invalid) {
      return;
    }

    this.passwordFormatError = this.validatePasswordFormat(this.password);
    if (this.passwordFormatError) {
      return;
    }

    // Block re-entry while a request is in flight: only one sign-in may be
    // active at a time, which removes the double-submit race entirely.
    if (this.loginPending) {
      return;
    }
    this.loginPending = true;

    this.plausible.trackEvent('login_click');

    // The vault MUST be populated before validateUser fires: the request goes
    // through ApiService.post → ensureValidToken → fetchNewToken, which reads
    // these credentials to obtain the API token for the sign-in call itself.
    // Failed attempts are wiped below so no invalid password stays persisted.
    this.credentialVault.setPassword(this.password);
    sessionStorage.setItem('LoginEmail', this.email);
    const params = {
      email: this.email,
      password: this.password,
    };
    // Latest-attempt token: rapid resubmits (double-click) can leave an older
    // request in flight; its late failure must not wipe the vault credentials
    // a newer successful attempt just persisted.
    const attempt = ++this.loginAttempt;
    this.loginService.validateUser(params).subscribe({
      next: (response) => {
        this.loginPending = false;
        if (attempt !== this.loginAttempt) {
          return; // stale response from a superseded attempt
        }
        if (response.status === 200 && response.body?.success) {
          // Clear any role flags a previous session left behind (e.g. navigating
          // back from OTP and signing in as a different user in the same tab) so
          // defaultDashboardPath$() cannot read another user's stale values while
          // this login's own role-flag chain is still in flight.
          this.userService.clearRoleFlags();
          this.router.navigate(['/otpVerification']);
          sessionStorage.setItem('userToken', 'logged-in');
          this.getUserDetails();
          this.getPTTokenForCookies();
        } else {
          // Rejected credentials must not linger in the vault.
          this.credentialVault.clear();
          this.errorMessage = response.body?.message || this.translate.instant('invalidCredentials');
        }
      },
      error: (error) => {
        this.loginPending = false;
        if (attempt !== this.loginAttempt) {
          return; // stale failure from a superseded attempt
        }
        // Failed/aborted attempts must not leave a persisted password behind.
        this.credentialVault.clear();
        this.errorMessage = error.error?.message || this.translate.instant('loginFailedTryAgain');
        console.error("Error while login:", error);
      }
    });
  }


  /**
  * Fetches and stores the current user's role details from the backend service.
  * @example
  * etUserRoleDetails()
  * void
  * @returns {void} No return value.
  **/
  getUserRoleDetails() {
    const params = {
      customer_id: this.userService.getCustomerId(),
      user_id: this.userService.getUserId(),
      is_sony_mgmt_user: false
    }
    this.commonService.getUserRoleDetails(params).subscribe({
      next: (response) => {

        // if (response.status === 200) {
        if (response && response.body && response.body.result !== null) {

          const apiResponse = response.body.result;

          this.userService.setDistinctRoles(apiResponse);
        }
      }
    });
  }

  getUserDetails() {
    const params = {
      user_email: this.email,
      customer_id: -1,
      common_user_id: -1
    }
     this.loginService.getUserDetails(params).subscribe({
    next: this.handleUserDetailsResponse.bind(this),
    error: this.handleLoginError.bind(this)
  });
  }

  /**
  * Handles a successful user details response by storing session data, updating user state, and loading additional user-specific configuration.
  * @example
  * handleUserDetailsResponse(response)
  * undefined
  * @param {any} response - The HTTP response object containing status and user details in the body.
  * @returns {void} No return value.
  **/
  private handleUserDetailsResponse(response: any): void {
  if (response.status !== 200 || !response.body) {
    console.error('Unexpected response:', response);
    // Terminal branch: setUserServiceData()/getCustomisationList() never run for this
    // response, so resolve the role-flag chain here rather than leaving it unset
    // (defaultDashboardPath$() would otherwise wait out its full readiness timeout).
    this.resetAllRoleFlags();
    return;
  }

  const user = response.body;
  this.setSessionStorage(user);
  this.setUserServiceData(user);
  // this.initializeDefaultDates();
  this.getCustomisationList();
  this.getUserRoleDetails();
}

private handleLoginError(error: any): void {
  console.error("Error while login:", error);
  console.error("Error message:", error.error?.message || "Unknown error");
  // Same reasoning as handleUserDetailsResponse's early-return branch above.
  this.resetAllRoleFlags();
}

/**
 * Resets isHead/isSpniUser/isTrentUser to false so a terminal login failure
 * (before any of the three are ever set) resolves the role-flag chain that
 * `defaultDashboardPath$()` waits on, instead of stalling it out to its
 * full readiness timeout on every failure.
 * @returns {void} No return value.
 */
private resetAllRoleFlags(): void {
  this.userService.setIsHead(false);
  this.userService.setSpniUser(false);
  this.userService.setTrentUser(false);
}

/**
 * Resets isSpniUser/isTrentUser to false. Used by the customisation-list
 * terminal branches, which run after isHead has already been resolved by
 * {@link getUserRoleDetails} -- see {@link resetAllRoleFlags} for the
 * earlier branches that reset all three.
 * @returns {void} No return value.
 */
private resetSpniTrentRoleFlags(): void {
  this.userService.setSpniUser(false);
  this.userService.setTrentUser(false);
}

/**
 * Stores login/session-related values in sessionStorage and initializes default date and compliance settings.
 * @example
 * setSessionStorage(body)
 * undefined
 * @param {any} body - Object containing user and session flags used to populate sessionStorage.
 * @returns {void} Does not return a value.
 */
private setSessionStorage(body: any): void {
  sessionStorage.setItem('is_certificate_visible', JSON.stringify(body.is_certificate_visible));
  sessionStorage.setItem('LoginUserName', body.name);
  sessionStorage.setItem('userEmail', this.email);
  sessionStorage.setItem('tawk_hash', body.tawk_hash);

  sessionStorage.setItem('is_notification_dot', body.notification_dot_visible);
  sessionStorage.setItem('is_messagecenter_dot', body.message_center_dot_visible);

  setTimeout(() => {
    sessionStorage.setItem('is_certificate_entity', JSON.stringify(body.is_certificate_entity));
  }, 0);

  if (body.hasOwnProperty('is_penalty_not_display_customer')) {
    sessionStorage.setItem('isPenaltyCardVisible', body.is_penalty_not_display_customer ? 'true' : 'false');
  }

  if (!sessionStorage.getItem('selected_compliance_type')) {
    sessionStorage.setItem('selected_compliance_type', 'Statutory');
  }

  if (!sessionStorage.getItem('from_date')) {
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    sessionStorage.setItem('from_date', firstDay.toISOString().split('T')[0]);
  }

  if (!sessionStorage.getItem('to_date')) {
    const currentDate = new Date();
    sessionStorage.setItem('to_date', currentDate.toISOString().split('T')[0]);
  }
}

/**
* Sets user service data and initializes visitor information from the provided body object.
* @example
* setUserServiceData(body)
* void
* @param {any} body - Object containing user and permission-related properties.
* @returns {void} Updates internal user service state without returning a value.
**/
private setUserServiceData(body: any): void {
  applyUserProfile(this.userService, body);

  this.tawkService.setVisitor(
    body.name,
    this.email,
    body.tawk_hash
  );
}

getCustomisationList() {
  const params = {
    customer_id: this.userService.getCustomerId()
  };

  this.loginService.getCustomisationList(params).subscribe({
    next: this.handleCustomisationResponse.bind(this),
    error: this.handleCustomisationError.bind(this)
  });
}

/**
 * Handles the customisation response, stores values in session storage, and processes the result array when present.
 * @example
 * handleCustomisationResponse(response)
 * undefined
 * @param {any} response - The API response containing customisation data and role details.
 * @returns {void} This method does not return a value.
 **/
private handleCustomisationResponse(response: any): void {
  if (response && response.body && response.body.result !== null) {
    writeCustomizationFlags(response.body.result);
    const { result, login_details, assessor_role, performer_reviewer_role } = response.body;
    this.LoginDetails = login_details;
    this.AssessorRole = assessor_role;
    this.PerformerReviewerRole = performer_reviewer_role;

    if (Array.isArray(result)) {
      this.processCustomisationArray(result);
    } else {
      console.warn('result is not an array');
      sessionStorage.setItem('is_MGMT_Total', '0');
      sessionStorage.setItem('is_MGMT_Total_Unique', '0');
      // Terminal branch: the SPNI/Trent chain that defaultDashboardPath$() waits on
      // will never run for this response, so resolve it here instead of leaving it
      // unset (which would stall every login on the 3s readiness timeout).
      this.resetSpniTrentRoleFlags();
    }
  } else {
    console.error('Unexpected response:', response);
    this.resetSpniTrentRoleFlags();
  }
}

private handleCustomisationError(error: any): void {
  console.error("Error while fetching customisation list:", error);
  // Same reasoning as the non-array branch above: terminate the role-flag chain
  // so defaultDashboardPath$() doesn't wait out its full timeout on every failure.
  this.resetSpniTrentRoleFlags();
}

/**
 * Processes a customization array to detect MGMT flags, update user role states, and persist feature toggles in session storage.
 * @example
 * processCustomisationArray(['MGMT_Total', 'Enable3DGraphs'])
 * undefined
 * @param {any[]} result - Array of customization keys to inspect.
 * @returns {void} Does not return a value.
 */
private processCustomisationArray(result: any[]): void {
  const hasMGMTTotal = result.includes('MGMT_Total');
  const hasMGMTTotalUnique = result.includes('MGMT_Total_Unique');
  const Enable3DGraphs = result.includes('Enable3DGraphs');

  const spniUser =
    result.includes('MGMT_Sony') &&
    this.userService.getRole() === 'MGMT';

  const trentUser =
    result.includes('MGMT_Trent') &&
    this.userService.getRole() === 'MGMT';

  this.userService.setTrentUser(trentUser);
  this.userService.setSpniUser(spniUser);

  sessionStorage.setItem('Enable3DGraphs', Enable3DGraphs ? 'true' : 'false');
  sessionStorage.setItem('is_MGMT_Total', hasMGMTTotal ? '1' : '0');
  sessionStorage.setItem('is_MGMT_Total_Unique', hasMGMTTotalUnique ? '1' : '0');

  if (hasMGMTTotal) {
    console.warn('MGMT_Total is present');
  }

  if (hasMGMTTotalUnique) {
    console.warn('MGMT_Total_Unique is present');
  }

  if (!hasMGMTTotal && !hasMGMTTotalUnique) {
    console.warn('Neither MGMT_Total nor MGMT_Total_Unique found');
  }
}

  /**
   * Validates the email field, sets submission state, and updates error messages or validation status.
   * @example
   * verifyEmail()
   * undefined
   * @returns {void} No return value; updates component state and may trigger email validation.
   **/
  verifyEmail() {
    this.isSubmitted = true;
    this.errorMessage = '';

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!this.email || this.email.trim() === '') {
      this.errorMessage = this.translate.instant('app.login.email_cannot_be_empty');
      return;
    }

    if (!emailRegex.test(this.email)) {
      this.errorMessage = this.translate.instant('app.login.invalid_email_format');
      return;
    }

    const body = { email: this.email };

    this.loginService.validateEmail(body).subscribe({
      next: (response) => {
        if (response.status === 200 && response.body?.message) {
          this.showEmailValidator = true;
          this.errorMessage = response.body.message;
        }
      },
      error: (error) => {
        this.errorMessage = error.error?.message || 'Invalid email. Please try again.';
      }
    });
  }

  signInWithSSO() {
    this.router.navigate(['/ssoLogin']);
  }

  unlockAccount() {
    this.router.navigate(['/unlockAccount']);
  }

  closePopup(): void {
    this.router.navigate(['/dashboard']);
  }

  showResetPassword() {
    this.router.navigate(['/resetPassword']);
  }

  goBack() {
    this.router.navigate(['/emailVerification']);
  }

  /**
   * Fetches PT tokens from the login service and stores available AI tokens in session storage.
   * @example
   * etPTTokenForCookies()
   * undefined
   * @returns {void} No return value.
   */
  getPTTokenForCookies() {
    this.loginService.getPTTokenForCookies().subscribe({
      next: (response) => {
        if (response.status === 200 && response.body && response.body.result) {
          const { result } = response.body;
          const helpAIToken = result.find((item: any) => item.help_aitoken)?.help_aitoken || '';
          const regAIToken = result.find((item: any) => item.reg_aitoken)?.reg_aitoken || '';
          const sqlAIToken = result.find((item: any) => item.sqlaitoken)?.sqlaitoken || '';
          const docAIToken = result.find((item: any) => item.doc_aitoken)?.doc_aitoken || '';

          if (helpAIToken) {
            sessionStorage.setItem('helpAIToken', helpAIToken);
          }
          if (regAIToken) {
            sessionStorage.setItem('regAIToken', regAIToken);
          }
          if (sqlAIToken) {
            sessionStorage.setItem('sqlAIToken', sqlAIToken);
          }
          if (docAIToken) {
            sessionStorage.setItem('docAIToken', docAIToken);
          }
        }
      },
      error: (error) => {
        console.error("Error fetching PT token:", error);
      }
    });
  }

}